import { useRef, useEffect, useState, useCallback } from 'react'
import { Box, VStack, Text, Link } from '@chakra-ui/react';
import Map, { Source, Layer, MapRef, Popup } from 'react-map-gl/maplibre'
import type { MapLayerMouseEvent } from 'maplibre-gl'
import maplibregl from 'maplibre-gl'
import { getMapStyle, DEFAULT_VIEW_STATE, LAYER_IDS, SOURCE_IDS, MAP_LABEL_FONT } from '../../lib/mapConfig'
import type { TurfMapProps, HouseholdMarker } from '../../types/turfMap'
import { ContactStatus } from '../../types/turfMap'
import type { WorkplaceMarker } from '../../types/workplaceOverlay'
import { contactsToGeoJson, householdsToGeoJson, boundariesToGeoJson, computeBounds } from './turfMapGeoJson'
import { workplacesToGeoJson } from './workplaceUtils'
import { zoomScaledCountRadius, ZOOM_SCALED_STROKE_WIDTH } from './householdPaint'
import { WorkplacePopup } from './WorkplacePopup'
import {
  O4_LAYER_IDS, O4_SOURCE_IDS, ADDIN_DIAMOND_IMAGE, PATH_DASH_SEQUENCE,
  registerAddInDiamond, startDashAnimation, routeO4Click,
} from './turfMapLayers'
import { TURF_MAP_COLORS } from '../../theme/turfMapColors'
import 'maplibre-gl/dist/maplibre-gl.css'

const O4_CLICKABLE = new Set<string>([O4_LAYER_IDS.GHOST_SITES, O4_LAYER_IDS.GHOST_EXTRAS, O4_LAYER_IDS.ADDIN_DIAMONDS])

export function TurfMap({
  boundaries,
  contacts,
  households,
  workplaces,
  routeGeoJson,
  onMarkerClick,
  onHouseholdClick,
  onHouseholdContactClick,
  onWorkplaceContactClick,
  onWorksiteClick,
  onBoundaryClick,
  onMapLoad,
  fitToBounds = true,
  height = '500px',
  disableInteractions = false,
  stops,
  showPath = true,
  animatePath = false,
  ghosts,
  onGhostClick,
  onAddInClick,
  children,
}: TurfMapProps) {
  const mapRef = useRef<MapRef>(null)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [selectedHousehold, setSelectedHousehold] = useState<HouseholdMarker | null>(null)
  const [selectedWorkplace, setSelectedWorkplace] = useState<WorkplaceMarker | null>(null)
  const [popupCoords, setPopupCoords] = useState<[number, number] | null>(null)

  // Convert data to GeoJSON
  const boundaryGeoJson = boundaries ? boundariesToGeoJson(boundaries) : null
  const contactGeoJson = contacts ? contactsToGeoJson(contacts) : null
  const householdGeoJson = households ? householdsToGeoJson(households) : null
  const workplaceGeoJson = workplaces ? workplacesToGeoJson(workplaces) : null

  // Handle map load event
  const handleMapLoad = useCallback(() => {
    setIsMapLoaded(true)
    const map = mapRef.current?.getMap()
    if (map) {
      // [O4] The add-in diamond is drawn once; a style reload drops images, and MapLibre asks for
      // it back via styleimagemissing (registerAddInDiamond is a no-op if it is already there).
      const register = () => registerAddInDiamond(map, TURF_MAP_COLORS.addIn, TURF_MAP_COLORS.addInStroke)
      register()
      map.on('styleimagemissing', (e: { id: string }) => { if (e.id === ADDIN_DIAMOND_IMAGE) register() })
    }
    if (onMapLoad && mapRef.current) {
      onMapLoad(mapRef.current.getMap())
    }
  }, [onMapLoad])

  // [O4] Drive-path animation: paint-property writes on the map, never React state; cancelled on
  // unmount / when the path is hidden; never started under prefers-reduced-motion.
  const hasRoute = !!routeGeoJson
  useEffect(() => {
    if (!isMapLoaded || !mapRef.current || !hasRoute || !showPath || !animatePath) return
    return startDashAnimation(mapRef.current.getMap(), O4_LAYER_IDS.ROUTE_ANIM)
  }, [isMapLoaded, hasRoute, showPath, animatePath])

  // Auto-fit bounds when data changes
  useEffect(() => {
    if (!isMapLoaded || !mapRef.current || !fitToBounds) return

    const bounds = computeBounds(boundaries, contacts, households, routeGeoJson, workplaces)
    if (bounds) {
      mapRef.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15,
        duration: 500,
      })
    }
  }, [isMapLoaded, boundaries, contacts, households, workplaces, routeGeoJson, fitToBounds])

  // Handle marker/cluster clicks
  const handleMapClick = useCallback(
    (event: MapLayerMouseEvent) => {
      if (!event.features || event.features.length === 0) {
        // Clicked on empty map area - close popup
        setSelectedHousehold(null)
        setSelectedWorkplace(null)
        setPopupCoords(null)
        return
      }

      const feature = event.features[0]

      // [O4] Suggested stop (ghost) or add-in diamond
      const point = event.point ? { x: event.point.x, y: event.point.y } : undefined
      if (routeO4Click(feature.layer.id, feature.properties, point, { onGhostClick, onAddInClick })) return

      // Handle cluster click
      if (feature.layer.id === LAYER_IDS.CONTACTS_CLUSTERS) {
        const clusterId = feature.properties?.cluster_id
        const source = mapRef.current?.getMap().getSource(SOURCE_IDS.CONTACTS)

        if (source && 'getClusterExpansionZoom' in source) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ;(source as any).getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
            if (err || !event.lngLat) return

            mapRef.current?.flyTo({
              center: [event.lngLat.lng, event.lngLat.lat],
              zoom: zoom ?? 14,
              duration: 500,
            })
          })
        }
        return
      }

      // Handle unclustered contact click
      if (feature.layer.id === LAYER_IDS.CONTACTS_UNCLUSTERED && onMarkerClick) {
        const contactId = feature.properties?.id
        if (contactId) {
          onMarkerClick(contactId)
        }
        return
      }

      // Handle household click - show popup
      if (feature.layer.id === 'households-unclustered') {
        const householdKey = feature.properties?.householdKey
        const household = households?.find(h => h.householdKey === householdKey)

        if (household) {
          setSelectedHousehold(household)
          setSelectedWorkplace(null)
          setPopupCoords([household.longitude, household.latitude])
        }

        // Also trigger onHouseholdClick if provided
        if (onHouseholdClick && householdKey) {
          onHouseholdClick(householdKey)
        }
        return
      }

      // Handle workplace click - show popup
      if (feature.layer.id === LAYER_IDS.WORKPLACES_UNCLUSTERED) {
        const worksiteId = feature.properties?.worksiteId
        const workplace = workplaces?.find(w => w.worksiteId === worksiteId)

        if (workplace) {
          setSelectedWorkplace(workplace)
          setSelectedHousehold(null)
          setPopupCoords([workplace.longitude, workplace.latitude])
        }
        return
      }

      // Handle boundary click
      if (
        (feature.layer.id === LAYER_IDS.BOUNDARIES_FILL ||
          feature.layer.id === LAYER_IDS.BOUNDARIES_LINE) &&
        onBoundaryClick
      ) {
        const boundaryId = feature.properties?.id
        if (boundaryId) {
          onBoundaryClick(boundaryId)
        }
        return
      }
    },
    [onMarkerClick, onHouseholdClick, onBoundaryClick, onGhostClick, onAddInClick, households, workplaces]
  )

  // Change cursor on household hover
  const handleMapMouseMove = useCallback(
    (event: MapLayerMouseEvent) => {
      if (!event.features || event.features.length === 0) return

      const feature = event.features[0]

      // Change cursor to pointer when hovering over household or workplace markers
      if ((feature.layer.id === 'households-unclustered' || feature.layer.id === LAYER_IDS.WORKPLACES_UNCLUSTERED ||
        O4_CLICKABLE.has(feature.layer.id)) && mapRef.current) {
        mapRef.current.getMap().getCanvas().style.cursor = 'pointer'
      }
    },
    []
  )

  const handleMapMouseLeave = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.getMap().getCanvas().style.cursor = ''
    }
  }, [])

  return (
    <Box borderRadius="lg" overflow="hidden" shadow="md" height={height}>
      <Map
        ref={mapRef}
        mapLib={maplibregl}
        initialViewState={DEFAULT_VIEW_STATE}
        style={{ width: '100%', height: '100%' }}
        mapStyle={getMapStyle()}
        onLoad={handleMapLoad}
        onClick={disableInteractions ? undefined : handleMapClick}
        onMouseMove={disableInteractions ? undefined : handleMapMouseMove}
        onMouseLeave={handleMapMouseLeave}
        interactiveLayerIds={
          disableInteractions
            ? []
            : [
                LAYER_IDS.BOUNDARIES_FILL,
                LAYER_IDS.BOUNDARIES_LINE,
                LAYER_IDS.CONTACTS_CLUSTERS,
                LAYER_IDS.CONTACTS_UNCLUSTERED,
                LAYER_IDS.WORKPLACES_UNCLUSTERED,
                'households-unclustered',
                ...(ghosts && onGhostClick ? [O4_LAYER_IDS.GHOST_SITES, O4_LAYER_IDS.GHOST_EXTRAS] : []),
                ...(stops && onAddInClick ? [O4_LAYER_IDS.ADDIN_DIAMONDS] : []),
              ]
        }
        dragPan={!disableInteractions}
        dragRotate={!disableInteractions}
        scrollZoom={!disableInteractions}
        doubleClickZoom={!disableInteractions}
        touchZoomRotate={!disableInteractions}
      >
        {/* Turf Boundaries */}
        {boundaryGeoJson && (
          <Source id={SOURCE_IDS.BOUNDARIES} type="geojson" data={boundaryGeoJson}>
            <Layer
              id={LAYER_IDS.BOUNDARIES_FILL}
              type="fill"
              paint={{
                'fill-color': ['coalesce', ['get', 'color'], '#3182ce'],
                'fill-opacity': 0.15,
              }}
            />
            <Layer
              id={LAYER_IDS.BOUNDARIES_LINE}
              type="line"
              paint={{
                'line-color': ['coalesce', ['get', 'color'], '#3182ce'],
                'line-width': 2,
                'line-dasharray': [2, 2],
              }}
            />
          </Source>
        )}

        {/* Contact Markers with Clustering */}
        {contactGeoJson && (
          <Source
            id={SOURCE_IDS.CONTACTS}
            type="geojson"
            data={contactGeoJson}
            cluster={true}
            clusterMaxZoom={14}
            clusterRadius={50}
          >
            {/* Cluster Circles */}
            <Layer
              id={LAYER_IDS.CONTACTS_CLUSTERS}
              type="circle"
              filter={['has', 'point_count']}
              paint={{
                'circle-color': '#3182ce',
                'circle-radius': [
                  'step',
                  ['get', 'point_count'],
                  15, // radius when count < 10
                  10,
                  20, // radius when count < 50
                  50,
                  25, // radius when count < 100
                  100,
                  30, // radius when count >= 100
                ],
                'circle-opacity': 0.8,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff',
              }}
            />

            {/* Cluster Count Labels */}
            <Layer
              id={LAYER_IDS.CONTACTS_CLUSTER_COUNT}
              type="symbol"
              filter={['has', 'point_count']}
              layout={{
                'text-field': '{point_count_abbreviated}',
                'text-font': MAP_LABEL_FONT,
                'text-size': 12,
              }}
              paint={{
                'text-color': '#ffffff',
              }}
            />

            {/* Unclustered Contact Markers */}
            <Layer
              id={LAYER_IDS.CONTACTS_UNCLUSTERED}
              type="circle"
              filter={['!', ['has', 'point_count']]}
              paint={{
                'circle-color': [
                  'case',
                  ['get', 'isVisited'],
                  '#48bb78', // green for visited
                  '#f56565', // red for not visited
                ],
                'circle-radius': 8,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff',
              }}
            />
          </Source>
        )}

        {/* Household Markers (grouped contacts) */}
        {householdGeoJson && (
          <Source
            id="households"
            type="geojson"
            data={householdGeoJson}
            cluster={false}
          >
            {/* Household Circles */}
            <Layer
              id="households-unclustered"
              type="circle"
              paint={{
                'circle-color': ['get', 'color'],
                // Zoom-scaled (2-3px at metro zoom -> 10-20px at street zoom), count-scaled within.
                'circle-radius': zoomScaledCountRadius('contactCount'),
                'circle-stroke-width': ZOOM_SCALED_STROKE_WIDTH,
                'circle-stroke-color': '#fff',
                'circle-opacity': 0.8,
              }}
            />
          </Source>
        )}

        {/* Household Click Popup */}
        {selectedHousehold && popupCoords && (
          <Popup
            longitude={popupCoords[0]}
            latitude={popupCoords[1]}
            closeButton={true}
            closeOnClick={false}
            anchor="bottom"
            offset={15}
            onClose={() => {
              setSelectedHousehold(null)
              setPopupCoords(null)
            }}
          >
            <VStack align="stretch" gap={1} minW="200px">
              <Text fontSize="sm" fontWeight="bold">
                {selectedHousehold.streetAddress}
              </Text>
              <Text fontSize="xs" color="gray.600">
                {selectedHousehold.city}
                {selectedHousehold.state ? `, ${selectedHousehold.state}` : ''} {selectedHousehold.zip}
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                {selectedHousehold.contactCount} {selectedHousehold.contactCount === 1 ? 'contact' : 'contacts'} •{' '}
                {selectedHousehold.visitedCount - selectedHousehold.needsRevisitCount} reached
                {selectedHousehold.needsRevisitCount > 0 && (
                  <Text as="span" color="orange.500"> • {selectedHousehold.needsRevisitCount} need revisit</Text>
                )}
              </Text>
              {selectedHousehold.contacts.length > 0 && (
                <VStack align="stretch" gap={0} mt={1} fontSize="xs">
                  {selectedHousehold.contacts.map(contact => (
                    <Link
                      key={contact.contactId}
                      color="accent"
                      _hover={{ textDecoration: 'underline' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (onHouseholdContactClick) {
                          onHouseholdContactClick(contact.contactId)
                        }
                      }}
                    >
                      • {contact.contactName}{' '}
                      {contact.isVisited
                        ? (contact.contactStatus === ContactStatus.NoAnswer || contact.contactStatus === ContactStatus.Callback)
                          ? '↩'
                          : '✓'
                        : ''}
                    </Link>
                  ))}
                </VStack>
              )}
            </VStack>
          </Popup>
        )}

        {/* Workplace Markers (grouped contacts at same business) */}
        {workplaceGeoJson && (
          <Source
            id={SOURCE_IDS.WORKPLACES}
            type="geojson"
            data={workplaceGeoJson}
            cluster={false}
          >
            <Layer
              id={LAYER_IDS.WORKPLACES_UNCLUSTERED}
              type="circle"
              paint={{
                'circle-color': ['get', 'color'],
                'circle-radius': zoomScaledCountRadius('employeeCount'),
                'circle-stroke-width': ZOOM_SCALED_STROKE_WIDTH,
                'circle-stroke-color': '#fff',
                'circle-opacity': 0.8,
              }}
            />
          </Source>
        )}

        {/* Workplace Click Popup */}
        {selectedWorkplace && popupCoords && (
          <Popup
            longitude={popupCoords[0]}
            latitude={popupCoords[1]}
            closeButton={true}
            closeOnClick={false}
            anchor="bottom"
            offset={15}
            onClose={() => {
              setSelectedWorkplace(null)
              setPopupCoords(null)
            }}
          >
            <WorkplacePopup
              workplace={selectedWorkplace}
              onContactClick={onWorkplaceContactClick}
              onWorksiteClick={onWorksiteClick}
            />
          </Popup>
        )}

        {/* Route Line */}
        {routeGeoJson && showPath && (
          <Source id={SOURCE_IDS.ROUTE} type="geojson" data={routeGeoJson}>
            <Layer
              id={LAYER_IDS.ROUTE_LINE}
              type="line"
              layout={{
                'line-cap': 'round',
                'line-join': 'round',
              }}
              paint={{
                'line-color': '#ed8936',
                'line-width': 3,
              }}
            />
          </Source>
        )}

        {/* [O4] Animated dashes over the drive path (only union campaign turfs pass animatePath) */}
        {routeGeoJson && showPath && animatePath && (
          <Source id={O4_SOURCE_IDS.ROUTE_ANIM} type="geojson" data={routeGeoJson}>
            <Layer
              id={O4_LAYER_IDS.ROUTE_ANIM}
              type="line"
              layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
              paint={{
                'line-color': TURF_MAP_COLORS.addInStroke,
                'line-width': 2,
                'line-dasharray': PATH_DASH_SEQUENCE[0],
              }}
            />
          </Source>
        )}

        {/* [O4] Numbered stops in walk order; add-ins as a violet diamond */}
        {stops && (
          <Source id={O4_SOURCE_IDS.STOPS} type="geojson" data={stops}>
            <Layer
              id={O4_LAYER_IDS.STOP_CIRCLES}
              type="circle"
              filter={['!', ['get', 'isAddIn']]}
              paint={{
                'circle-color': TURF_MAP_COLORS.route,
                'circle-radius': 10,
                'circle-stroke-width': 2,
                'circle-stroke-color': TURF_MAP_COLORS.addInStroke,
              }}
            />
            <Layer
              id={O4_LAYER_IDS.STOP_NUMBERS}
              type="symbol"
              filter={['!', ['get', 'isAddIn']]}
              layout={{
                'text-field': ['to-string', ['get', 'order']],
                'text-font': MAP_LABEL_FONT,
                'text-size': 11,
                'text-allow-overlap': true,
              }}
              paint={{ 'text-color': TURF_MAP_COLORS.addInStroke }}
            />
            <Layer
              id={O4_LAYER_IDS.ADDIN_DIAMONDS}
              type="symbol"
              filter={['get', 'isAddIn']}
              layout={{
                'icon-image': ADDIN_DIAMOND_IMAGE,
                'icon-allow-overlap': true,
                'text-field': ['to-string', ['get', 'order']],
                'text-font': MAP_LABEL_FONT,
                'text-size': 11,
                'text-allow-overlap': true,
              }}
              paint={{ 'text-color': TURF_MAP_COLORS.addInStroke }}
            />
          </Source>
        )}

        {/* [O4] Suggestions: a translucent "+" for a new site, a "+N" badge beside an existing stop */}
        {ghosts && (
          <Source id={O4_SOURCE_IDS.GHOSTS} type="geojson" data={ghosts}>
            <Layer
              id={O4_LAYER_IDS.GHOST_SITES}
              type="circle"
              filter={['!=', ['get', 'kind'], 'ExistingStopExtras']}
              paint={{
                'circle-color': TURF_MAP_COLORS.ghost,
                'circle-opacity': 0.45,
                'circle-radius': 11,
                'circle-stroke-width': 1.5,
                'circle-stroke-color': TURF_MAP_COLORS.ghost,
              }}
            />
            <Layer
              id={O4_LAYER_IDS.GHOST_SITE_PLUS}
              type="symbol"
              filter={['!=', ['get', 'kind'], 'ExistingStopExtras']}
              layout={{
                'text-field': '+',
                'text-font': MAP_LABEL_FONT,
                'text-size': 16,
                'text-allow-overlap': true,
              }}
              paint={{ 'text-color': TURF_MAP_COLORS.ghostText }}
            />
            <Layer
              id={O4_LAYER_IDS.GHOST_EXTRAS}
              type="circle"
              filter={['==', ['get', 'kind'], 'ExistingStopExtras']}
              paint={{
                'circle-color': TURF_MAP_COLORS.addIn,
                'circle-radius': 9,
                'circle-translate': [14, -14],
                'circle-stroke-width': 1.5,
                'circle-stroke-color': TURF_MAP_COLORS.addInStroke,
              }}
            />
            <Layer
              id={O4_LAYER_IDS.GHOST_EXTRAS_TEXT}
              type="symbol"
              filter={['==', ['get', 'kind'], 'ExistingStopExtras']}
              layout={{
                'text-field': ['concat', '+', ['to-string', ['get', 'count']]],
                'text-font': MAP_LABEL_FONT,
                'text-size': 10,
                'text-allow-overlap': true,
              }}
              paint={{ 'text-color': TURF_MAP_COLORS.addInStroke, 'text-translate': [14, -14] }}
            />
          </Source>
        )}

        {children}
      </Map>
    </Box>
  );
}
